import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export const SITE_URL = "https://tourismintel.ai";
export const ROBOTS_INDEX = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

function upsertMeta(selector, create, value) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    create(el);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

// Canonical + og:url + twitter:url always agree (the static og:url in index.html points to the
// homepage, which contradicted the runtime canonical on every SPA route — GSC "duplicate" signal).
export function setCanonical(pathOrUrl) {
  const path = pathOrUrl === "/index.html" ? "/" : pathOrUrl;
  const href = path.startsWith("http") ? path : `${SITE_URL}${path}`;
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  upsertMeta('meta[property="og:url"]', (m) => m.setAttribute("property", "og:url"), href);
  upsertMeta('meta[name="twitter:url"]', (m) => m.setAttribute("name", "twitter:url"), href);
}

export function setDescription(text) {
  const el = document.querySelector('meta[name="description"]');
  const og = document.querySelector('meta[property="og:description"]');
  const prev = el?.getAttribute("content");
  const prevOg = og?.getAttribute("content");
  el?.setAttribute("content", text);
  og?.setAttribute("content", text);
  return () => {
    if (prev) el?.setAttribute("content", prev);
    if (prevOg) og?.setAttribute("content", prevOg);
  };
}

// noindex ONLY on a confirmed 404 from the API — never on transient errors (Googlebot render).
export function setNoIndex() {
  const el = document.querySelector('meta[name="robots"]');
  const prev = el?.getAttribute("content") || ROBOTS_INDEX;
  el?.setAttribute("content", "noindex, follow");
  return () => el?.setAttribute("content", prev);
}

export function setAlternate(type, href) {
  let el = document.querySelector(`link[rel="alternate"][type="${type}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("type", type);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return () => el.remove();
}

export function setHreflang(hreflang, href) {
  let el = document.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return () => el.remove();
}

export function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
  return () => document.getElementById(id)?.remove();
}

// Static JSON-LD blocks in index.html that describe ONE page (homepage FAQ, podcast series) must not
// ride along on every SPA route (Google: structured data has to describe the page it is on).
const SCOPED_STATIC_LD = { "ld-faq": ["/"], "ld-podcast": ["/", "/podcast"] };
const parked = {};

function scopeStaticJsonLd(pathname) {
  for (const [id, routes] of Object.entries(SCOPED_STATIC_LD)) {
    const el = document.getElementById(id);
    const wanted = routes.includes(pathname);
    if (el && !wanted) {
      parked[id] = el.textContent;
      el.remove();
    } else if (!el && wanted && parked[id]) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = id;
      s.textContent = parked[id];
      document.head.appendChild(s);
    }
  }
}

// Route-level default: canonical always matches the current path (GSC fix —
// the old static tag pointed EVERY route to the homepage, so Google reported
// "Duplicate, Google chose different canonical than user" on public pages).
// Pages with special canonicals (e.g. newsletter UUID → slug) override later.
export function RouteCanonical() {
  const { pathname } = useLocation();
  useEffect(() => {
    // build-time prerendered head (scripts/prerender.mjs) is replaced by the live page's own tags
    document.querySelectorAll("[data-prerender]").forEach((el) => el.remove());
  }, []);
  useEffect(() => {
    const clean = pathname === "/" ? "/" : pathname.replace(/\/+$/, "");
    setCanonical(clean);
    scopeStaticJsonLd(clean);
  }, [pathname]);
  return null;
}
